/**
 * CDB-TQS API — Communicator 2.0 attachments (personal documents)
 *
 * Personal documents in Drive 2.0 come from the existing Communicator 2.0
 * attachment storage (tqs.communicator2_attachments) via the agent/v2
 * attachment endpoints instead of a separate BFF table + route.
 */

import { logApiCall, logApiResponse, logError } from "@/lib/logger";
import {
  CDB_TQS_API_BASE_URL,
  fetchWithLogging,
  getAuthHeaders,
  getMimeTypeFromFilename,
} from "./http";

export interface MyFileItem {
  id: string;
  file_name: string;
  mime_type: string;
  file_size: number;
  char_count: number;
  created_at: string | null;
  has_blob: boolean;
  /** Honcho personal-memory ingest state (#536): none | ingested | failed. */
  memory_status?: string;
  /** full | no_text (#536). */
  indexing_quality?: string;
}

export interface MyFilesResponse {
  items: MyFileItem[];
  total: number;
}

export const attachmentsApi = {
  async getMyFiles(
    params: {
      limit?: number;
      offset?: number;
      search?: string;
    } = {}
  ): Promise<MyFilesResponse> {
    const queryParams: Record<string, string> = {};
    if (params.limit != null) queryParams.limit = String(params.limit);
    if (params.offset != null) queryParams.offset = String(params.offset);
    if (params.search) queryParams.search = params.search;

    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/agent/v2/attachments/my-files`,
      { method: "GET", params: queryParams }
    );
    return response.data as MyFilesResponse;
  },

  /**
   * Download a personal attachment via the preview endpoint and create a blob URL
   * @param attachmentId - Attachment UUID
   * @param filename - Original filename, used to set the correct MIME type
   * @returns Blob URL for the attachment file
   */
  async getAttachmentFile(attachmentId: string, filename: string, signal?: AbortSignal): Promise<string> {
    const url = `${CDB_TQS_API_BASE_URL}/agent/v2/attachments/${attachmentId}/preview`;

    logApiCall("GET", url);

    try {
      const authHeaders = await getAuthHeaders();

      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          ...authHeaders,
        },
        signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to download attachment: ${response.status} ${response.statusText}`
        );
      }

      logApiResponse("GET", url, response.status, { filename });

      const blob = await response.blob();

      const mimeType = getMimeTypeFromFilename(filename);
      const typedBlob = mimeType && blob.type !== mimeType
        ? new Blob([blob], { type: mimeType })
        : blob;

      return URL.createObjectURL(typedBlob);
    } catch (error) {
      logError(error as Error, {
        method: "GET",
        url,
        context: "Attachment file download failed",
      });
      throw error;
    }
  },

  /**
   * Rename an uploaded attachment's display name (cdb_webos#612).
   *
   * The counterpart of `renamePersonalFile`. "Meine Dokumente" merges two
   * stores and which one a file landed in depends on how it arrived, not on
   * anything the user can see — offering rename in only one reads as "docx
   * cannot be renamed". The extension is kept server-side.
   */
  async renameAttachment(
    attachmentId: string,
    filename: string,
  ): Promise<{ renamed: boolean; filename: string }> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/agent/v2/attachments/${attachmentId}`,
      { method: "PATCH", body: JSON.stringify({ filename }) }
    );
    return response.data as { renamed: boolean; filename: string };
  },

  async deleteAttachment(attachmentId: string): Promise<{ deleted: boolean }> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/agent/v2/attachments/${attachmentId}`,
      { method: "DELETE" }
    );
    return response.data as { deleted: boolean };
  },

  /** Manually ingest a personal document into the user's Honcho memory (#536). */
  async ingestAttachment(
    attachmentId: string
  ): Promise<{ memory_status: string; indexing_quality: string }> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/agent/v2/attachments/${attachmentId}/ingest`,
      { method: "POST" }
    );
    return response.data as { memory_status: string; indexing_quality: string };
  },
};
